// ------------------ IMPORTS -------------------

import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import { Popover } from "@lockerpm/design";
import { ButtonV2 } from "#src/commonV2/Button";

// ------------------ TYPES -------------------

export interface TimeInDayPickerProps {
  value?: number; // milliseconds from 0 to 24 * 60 * 60 * 1000
  setValue?: (value: number) => void;
  placeholder?: string;
  disabled?: boolean;
}

// ------------------ MAIN COMPONENT -------------------

export const DateInMonthPicker: React.FC<TimeInDayPickerProps> = ({
  value,
  setValue,
  placeholder = "Select Date",
  disabled = false,
}) => {
  const { t } = useTranslation("endpoint", { keyPrefix: "passports" });

  // ------------------ HOOKS -------------------

  const [visible, setVisible] = useState<boolean>(false);

  // ------------------ DATA -------------------

  const [date, setDate] = useState<number>(value || 1);

  // ------------------ COMPUTED -------------------

  const displayText = useMemo(() => {
    if (!value) return placeholder;
    return `${t("modal.addAndEdit.time_frame.date_of_month", { date: value })}`;
  }, [value, placeholder, t]);

  // ------------------ EFFECTS -------------------

  // Sync internal state when value prop changes
  useEffect(() => {
    if (value !== undefined) {
      setDate(value);
    }
  }, [value]);

  // ------------------ METHODS -------------------

  const handleApply = useCallback((): void => {
    setValue?.(date);
    setVisible(false);
  }, [date, setValue]);

  const handleCancel = useCallback((): void => {
    // Reset to initial values
    if (value !== undefined) {
      setDate(value);
    }
    setVisible(false);
  }, [value]);

  const handleVisibilityChange = useCallback(
    (open: boolean): void => {
      if (!disabled) {
        setVisible(open);

        // Scroll to selected values when popover opens
        if (open && value !== undefined) {
          setDate(value);
        }
      }
    },
    [disabled, value]
  );

  // ------------------ RENDER -------------------

  const popoverContent = (
    <div className="v2">
      {/* Date Selector */}
      <div className="grid grid-cols-7 gap-1 p-2">
        {Array.from({ length: 31 }, (_, i) => (
          <div
            key={i + 1}
            onClick={() => setDate(i + 1)}
            className={`w-10 h-10 rounded-full flex items-center justify-center text-sm cursor-pointer ${
              date === i + 1
                ? "bg-primary text-white font-medium"
                : "bg-white text-gray-v2-700 hover:bg-gray-v2-100"
            }`}
          >
            {i + 1}
          </div>
        ))}
      </div>
      {/* Date Selector End */}

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4 border-t border-gray-200 mb-1">
        <ButtonV2
          variant="secondary"
          color="gray"
          size="md"
          onClick={handleCancel}
          className="flex-1"
        >
          {t("modal.addAndEdit.time_frame.cancel")}
        </ButtonV2>
        <ButtonV2 size="md" onClick={handleApply} className="flex-1">
          {t("modal.addAndEdit.time_frame.apply")}
        </ButtonV2>
      </div>
      {/* Action Buttons End */}
    </div>
  );

  return (
    <Popover
      content={popoverContent}
      trigger="click"
      open={visible}
      onOpenChange={handleVisibilityChange}
    >
      <ButtonV2
        variant="secondary"
        color="gray"
        size="md"
        disabled={disabled}
        className="w-full"
        contentFloatLeft
      >
        <span>{displayText}</span>
      </ButtonV2>
    </Popover>
  );
};
